import { Add, History, Lock } from '@mui/icons-material'
import { Button, IconButton, Tooltip, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useEffect, useState } from 'react'
import { useAuth, useEventTracking } from '../../../../hooks'
import { EventType, Permission } from '../../../../constants'
import { AvailabilityConstraintEventData } from '../../../../types'
import { useTranslation } from 'react-i18next'
import { HistoryModal } from './HistoryModal'

interface Props {
  onAddAvailableTimeClick: () => void
  onAddBlockedTimeClick: () => void
  disabled?: boolean
}

const ButtonsContainer = styled('div')`
  align-items: center;
  display: grid;
  gap: 1rem;
  grid-auto-flow: column;
  grid-template-rows: 1fr;
  justify-content: end;
`

const ViewOnlyLabel = styled('div')`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: gray;
  font-size: 0.875rem;
  padding: 0 1rem;
`

const Actions = ({ onAddBlockedTimeClick, onAddAvailableTimeClick, disabled }: Props) => {
  const { user } = useAuth()
  const { t } = useTranslation()
  const { track: trackEvent } = useEventTracking()
  const [isHistoryOpen, beHistoryOpen] = useState(false)
  const onHistoryClose = () => beHistoryOpen(false)
  const userPermissions = user?.permissions ?? new Set<string>()
  const canManageAvailabilityConstraints = userPermissions.has(Permission.AVAILABILITY_CONSTRAINTS_WRITE)

  useEffect(() => {
    const sessionStorageKey = 'availabilityConstraintViewedEffectRun'

    // Early exit if the effect has already run or permissions are not loaded
    if (sessionStorage.getItem(sessionStorageKey) || userPermissions.size === 0) {
      return
    }

    // Track the event
    const data: AvailabilityConstraintEventData = {
      accessMode: canManageAvailabilityConstraints ? 'EDIT' : 'VIEW_ONLY',
    }
    trackEvent(EventType.AVAILABILITY_CONSTRAINT_VIEWED, { data })

    // Mark the effect as run
    sessionStorage.setItem(sessionStorageKey, 'true')
  }, [trackEvent, canManageAvailabilityConstraints, userPermissions])

  const opacity = disabled ? '.5' : '1'

  return (
    <ButtonsContainer style={{ opacity }}>
      {canManageAvailabilityConstraints ? (
        <>
          <Tooltip arrow placement="top" title={t('pages.settings.resources.availabilityConstraints.button.constraints_history')}>
            <IconButton size="small" onClick={() => beHistoryOpen(true)} color="primary">
              <History />
            </IconButton>
          </Tooltip>
          <Button color="primary" onClick={onAddBlockedTimeClick} disabled={disabled} startIcon={<Add />} variant="outlined">
            {t('pages.settings.resources.availabilityConstraints.button.addBlockedTime')}
          </Button>
          <Button color="primary" onClick={onAddAvailableTimeClick} disabled={disabled} startIcon={<Add />} variant="contained">
            {t('pages.settings.resources.availabilityConstraints.button.addAvailableTime')}
          </Button>
          <HistoryModal isOpen={isHistoryOpen} onClose={onHistoryClose} />
        </>
      ) : (
        <ViewOnlyLabel>
          <Lock fontSize="small" />
          <Typography variant="body2">{t('pages.settings.resources.availabilityConstraints.accessMessage')}</Typography>
        </ViewOnlyLabel>
      )}
    </ButtonsContainer>
  )
}

export { Actions }
